<template>
    <auth-layout v-if="isPageReady" :image="3" :progress="progress" :isProgressBarHidden="!!lastRegisteredPhysician">
        <div v-if="!!lastRegisteredPhysician" class="register-physicians__icon">
            <common-icon name="success" size="ultra-large"></common-icon>
        </div>
        <common-content-spacer height="huge">
            <common-content-wrapper width="small">
                <common-content-spacer height="small">
                    <common-page-header
                        :isCenter="!!lastRegisteredPhysician"
                        :heading="pageHeading"
                        :content="pageDescription"
                    ></common-page-header>
                    <span v-if="lastRegisteredPhysician" class="page-header page-header__content page-header--is-center">
                        <br />
                        <p v-html="titleForSettingPassword"></p>
                    </span>

                    <button
                        v-if="showRegisterPhysician || showInvitePhysicians"
                        class="form-repeater__button"
                        type="button"
                        @click="goBack"
                    >
                        &lt; {{ $t('pages.auth.signup.steps.register_physicians.buttons.back') }}
                    </button>
                    <button
                        v-else-if="!lastRegisteredPhysician && !pendingRegitrationPhysician"
                        class="form-repeater__button"
                        type="button"
                        @click="skipForNow"
                    >
                        {{ $t('pages.auth.signup.steps.connecting.form.skip_link_label') }} &gt;
                    </button>
                </common-content-spacer>
            </common-content-wrapper>

            <common-content-wrapper v-if="pendingRegitrationPhysician" width="small">
                <user-card
                    :physician="pendingRegitrationPhysician"
                    size="large"
                    :showRefCount="false"
                    :showSpeciality="false"
                    :isUserProfileLinkEnabled="false"
                >
                    <div class="eligible-physician-hover-menu">
                        <div class="card__role--large">{{ practice.city }}, {{ practice.state }}</div>
                        <common-specialities
                            :specialities="pendingRegitrationPhysician.specialities"
                            :showSpecialityCount="true"
                        ></common-specialities>

                        <common-buttons class="card__set-availability-btn">
                            <common-button
                                :label="$t('pages.auth.signup.steps.register_physicians.set_availability')"
                                type="primary"
                                @click="goToPhysicianAvailability"
                            ></common-button>
                        </common-buttons>
                    </div>
                </user-card>
            </common-content-wrapper>

            <common-content-wrapper v-show="showRegisterPhysicians" width="small">
                <common-content-spacer height="huge">
                    <common-buttons>
                        <common-button
                            :label="
                                lastRegisteredPhysician
                                    ? $t('pages.auth.signup.steps.register_physicians.buttons.register_another')
                                    : $t('pages.auth.signup.steps.register_physicians.buttons.register_physicians')
                            "
                            :type="lastRegisteredPhysician ? 'secondary' : 'primary'"
                            class="register-physicians__button"
                            size="large"
                            @click="registerPhysician"
                        ></common-button>
                    </common-buttons>

                    <div class="auth-invite-admin-register-practice__text">
                        <common-divider class="register-physicians">
                            <p>
                                {{
                                    lastRegisteredPhysician
                                        ? $t('pages.auth.signup.steps.register_physicians.or_finalize')
                                        : $t('pages.auth.signup.steps.register_physicians.or_invite')
                                }}
                            </p>
                        </common-divider>
                    </div>
                    <footer>
                        <common-buttons>
                            <common-button
                                v-if="lastRegisteredPhysician"
                                size="large"
                                :label="$t('pages.auth.signup.steps.register_physicians.buttons.finalize_registration')"
                                class="register-physicians__button"
                                type="primary"
                                @click="skipForNow"
                            ></common-button>
                            <common-button
                                v-else
                                :label="$t('pages.auth.signup.steps.register_physicians.buttons.invite_physicians')"
                                class="register-physicians__button"
                                size="large"
                                type="secondary"
                                @click="invitePhysicians"
                            ></common-button>
                        </common-buttons>
                    </footer>
                </common-content-spacer>
            </common-content-wrapper>
        </common-content-spacer>

        <common-content-wrapper v-show="showRegisterPhysician" width="small">
            <register-physician-form :specialities="specialities"></register-physician-form>
        </common-content-wrapper>

        <common-content-wrapper v-show="showInvitePhysicians" width="small">
            <auth-admin-invite-physicians @next-step="skipForNow"></auth-admin-invite-physicians>
        </common-content-wrapper>
    </auth-layout>
</template>

<script>
    import SpecialitiesAPI from 'api/SpecialitiesAPI'
    import SkipStepService from 'services/SkipStepService'
    import SignupPageMixin from 'mixins/SignupPageMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AuthLayout from 'components/layouts/AuthLayout'
    import RegisterPhysicianForm from 'components/forms/auth/AuthRegisterPhysicianForm'
    import AuthAdminInvitePhysicians from 'components/page-elements/auth/AuthAdminInvitePhysicians'
    import AuthService from 'services/AuthService'
    import UserCard from 'components/page-elements/referrals/UserCard'
    import UserRolesEnum from '../../../enums/UserRolesEnum'

    export default {
        mixins: [SignupPageMixin, PageWithDataMixin],

        components: {
            AuthLayout,
            RegisterPhysicianForm,
            AuthAdminInvitePhysicians,
            UserCard,
        },

        data() {
            return {
                specialities: [],
                showRegisterPhysician: false,
                showInvitePhysicians: false,
                lastRegisteredPhysician: AuthService.getLastRegisteredPhysician(),
                pendingRegitrationPhysician: null,
                practice: this.$auth.getPractice(),
            }
        },

        loadData() {
            return {
                specialities: SpecialitiesAPI.getListMultiselect(),
            }
        },

        computed: {
            pageDescription() {
                if (this.lastRegisteredPhysician) {
                    return this.lastRegisteredPhysician.isPasswordSet
                        ? this.$t('pages.auth.signup.steps.register_physicians.description_physician_completed', {
                              name: `${this.lastRegisteredPhysician.firstName} ${this.lastRegisteredPhysician.lastName}`,
                          })
                        : ''
                }
                return this.showRegisterPhysician
                    ? this.$t('pages.auth.signup.steps.register_physicians.register_description')
                    : this.showInvitePhysicians
                    ? this.$t('pages.auth.signup.steps.register_physicians.invite_description')
                    : this.$t('pages.auth.signup.steps.register_physicians.description')
            },

            pageHeading() {
                return this.lastRegisteredPhysician
                    ? this.$t('pages.auth.signup.steps.register_physicians.heading_physician_completed', {
                          name: `${this.lastRegisteredPhysician.firstName} ${this.lastRegisteredPhysician.lastName}`,
                      })
                    : this.$t('pages.auth.signup.steps.register_physicians.heading')
            },

            titleForSettingPassword() {
                return this.lastRegisteredPhysician.isPasswordSet
                    ? ''
                    : this.$t('pages.auth.signup.steps.register_physicians.description_physician_completed_no_password', {
                          email: this.lastRegisteredPhysician.email,
                          name: `${this.lastRegisteredPhysician.firstName} ${this.lastRegisteredPhysician.lastName}`,
                      })
            },

            showRegisterPhysicians() {
                return !this.showRegisterPhysician && !this.showInvitePhysicians && !this.pendingRegitrationPhysician
            },
        },

        methods: {
            skipForNow() {
                SkipStepService.hideStep('registerPhysicians')
                const loggedInUser = this.$auth.getUser()
                this.$auth.setActiveMember(loggedInUser)
                this.goToNextStep()
            },

            goBack() {
                this.showRegisterPhysician = false
                this.showInvitePhysicians = false
            },

            registerPhysician() {
                this.lastRegisteredPhysician = null
                this.showRegisterPhysician = true
            },

            invitePhysicians() {
                this.showInvitePhysicians = true
            },

            checkForPendingRegistrationPhysician() {
                this.pendingRegitrationPhysician = this.$auth
                    .getMembers()
                    .find((m) => !m.isAvailabilitySet && m.userRole.id === UserRolesEnum.PHYSICIAN.ID)
            },

            goToPhysicianAvailability() {
                this.$auth.setActiveMember(this.pendingRegitrationPhysician)
                this.$router.push({ name: 'auth.signup.schedule' })
            },
        },

        mounted() {
            this.checkForPendingRegistrationPhysician()
        },
    }
</script>
